import { loadOverview } from '@/data/queries'
import type { FlagGroup } from '@/data/queries'
import { useAsync } from '@/data/useAsync'
import { Card, CompositionBar, Failed, Loading, SeverityPill, Tile } from '@/components/bits'
import { Icon } from '@/components/Icon'
import { useOrg } from '@/org/OrgProvider'
import { plural, timeOfDay } from '@/lib/format'

/**
 * Rule codes are how the back end talks; these are how people talk.
 * A code with no entry here falls back to the code itself, which is
 * ugly on purpose so a missing label gets noticed and fixed.
 */
const RULE_LABEL: Record<string, string> = {
  rtw_missing: 'Right to work check missing',
  rtw_follow_up_due: 'Right to work follow-up due',
  visa_expiry: 'Visa expiring',
  unconfirmed_key_date: 'Date needs confirming',
  milestone_due: 'Milestone approaching',
  sms_report_due: 'Sponsor management report due',
}

const RULE_DETAIL: Record<string, string> = {
  rtw_missing:
    'No completed check is recorded here. An eligibility value in Planday is a claim from a spreadsheet, not evidence anyone has seen, so it does not give a statutory excuse.',
  unconfirmed_key_date:
    'These dates were defaulted rather than received. They are stored as defaulted so nothing downstream treats them as fact. Confirming one fixes it against future syncs.',
  milestone_due: 'Probation ends, service anniversaries and fixed-term ends in the next 60 days.',
  rtw_follow_up_due: 'Time-limited permission to work is running out and needs re-checking.',
  visa_expiry: 'A recorded visa expiry is approaching.',
  sms_report_due: 'A change reportable to the Home Office within the sponsor duties window.',
}

const SEVERITY_COLOUR: Record<string, string> = {
  critical: 'var(--sev-critical)',
  overdue: 'var(--sev-overdue)',
  due: 'var(--sev-due)',
  due_soon: 'var(--sev-soon)',
  info: 'var(--sev-info)',
}

const DEPT_COLOURS = [
  'var(--copper)',
  'var(--copper-deep)',
  'var(--sev-due)',
  'var(--sev-soon)',
  'var(--sev-info)',
  'var(--ivory-faint)',
  'var(--raised)',
]

function FlagRow({ group }: { group: FlagGroup }) {
  const label = RULE_LABEL[group.ruleCode] ?? group.ruleCode
  const detail = RULE_DETAIL[group.ruleCode]
  const remainder = group.total - group.sample.length

  return (
    <div className="flagrow">
      <div className="stripe" style={{ background: SEVERITY_COLOUR[group.severity] }} />
      <div className="grow">
        <div className="title">
          <strong>{label}</strong>
          <SeverityPill severity={group.severity} />
          <span className="count">{plural(group.total, 'person', 'people')}</span>
          {group.total > 50 && <span className="tag">Backlog &middot; task suppressed</span>}
        </div>
        {detail !== undefined && <p>{detail}</p>}
        {group.sample.length > 0 && (
          <div className="names">
            {group.sample.map((n, i) => (
              <span key={n}>
                {i > 0 && ', '}
                <b>{n}</b>
              </span>
            ))}
            {remainder > 0 && ` and ${remainder} more`}
          </div>
        )}
      </div>
    </div>
  )
}

export function Today() {
  const { current } = useOrg()
  const result = useAsync((signal) => {
    void signal
    return loadOverview(current.id)
  }, [current.id])

  if (result.state === 'loading') return <Loading what="today" />
  if (result.state === 'error') return <Failed message={result.message} onRetry={result.reload} />

  const o = result.data
  const staffed = o.departments.filter((d) => d.headcount > 0)
  const biggest = Math.max(1, ...o.departments.map((d) => d.headcount))

  return (
    <div className="screen">
      <div className="tiles">
        <Tile label="Active headcount" value={o.headcount.active}>
          <div className="meter" role="img" aria-label={`${staffed.length} staffed departments`}>
            {staffed.map((d, i) => (
              <i
                key={d.name}
                style={{
                  width: `${(d.headcount / Math.max(1, o.headcount.active)) * 100}%`,
                  background: DEPT_COLOURS[i % DEPT_COLOURS.length],
                }}
              />
            ))}
          </div>
          <div className="note">
            Across {plural(staffed.length, 'staffed department')}, {o.departments.length} mirrored
          </div>
        </Tile>

        <Tile label="Open flags" value={o.flags.total}>
          <CompositionBar
            label="Open flags by severity"
            parts={(['critical', 'overdue', 'due', 'due_soon', 'info'] as const)
              .filter((s) => (o.flags.bySeverity[s] ?? 0) > 0)
              .map((s) => ({
                width: o.flags.bySeverity[s] ?? 0,
                colour: SEVERITY_COLOUR[s]!,
                label: s === 'due_soon' ? 'due soon' : s,
              }))}
          />
        </Tile>

        <Tile label="Open tasks" value={o.tasks.total}>
          <div className="note">
            {Object.entries(o.tasks.byRole)
              .map(([role, n]) => `${n} with ${role}`)
              .join(', ') || 'Nothing outstanding'}
          </div>
        </Tile>

        <Tile label="Dates needing confirming" value={o.unconfirmedDates}>
          <div className="meter" role="img" aria-label="Share of headcount with a defaulted date">
            <i
              style={{
                width: `${(o.unconfirmedDates / Math.max(1, o.headcount.active)) * 100}%`,
                background: 'var(--sev-overdue)',
              }}
            />
          </div>
          <div className="note">Start dates we guessed rather than received</div>
        </Tile>
      </div>

      <div className="cols" style={{ marginTop: 20 }}>
        <div className="stack">
          <Card
            title="Needs attention"
            flush
            aside={<span className="eyebrow">Rolled up &middot; 3 named per rule</span>}
          >
            {o.flags.groups.length === 0 ? (
              <div className="empty">
                <p>Nothing open. Worth checking the rules actually ran.</p>
              </div>
            ) : (
              o.flags.groups.map((g) => <FlagRow key={`${g.ruleCode}:${g.severity}`} group={g} />)
            )}
          </Card>

          {o.exemptions.length > 0 && (
            <Card
              title="Compliance exemptions"
              aside={<span className="mono" style={{ fontSize: 12.5, color: 'var(--ivory-dim)' }}>{plural(o.exemptions.length, 'person', 'people')}</span>}
            >
              <div className="callout" style={{ marginBottom: 14 }}>
                <strong>Exempt from a flag is not exempt from the law.</strong> An exemption
                suppresses the reminder and nothing else. Each one carries a written reason, and
                the reason is what appears in the audit trail.
              </div>
              <div className="tablewrap">
                <table>
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Reason recorded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.exemptions.map((e) => (
                      <tr key={e.personId}>
                        <td className="name">{e.name}</td>
                        <td style={{ color: 'var(--ivory-dim)' }}>{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        <div className="stack">
          <Card title="Planday" flush>
            <div className="queue">
              <div className={o.writesAllowed ? 'qrow done' : 'qrow held'}>
                <Icon name={o.writesAllowed ? 'check' : 'hold'} size={18} />
                <div className="grow">
                  {o.writesAllowed ? 'Writes allowed' : 'Writes held'}
                  <small>
                    Two switches must both be on: <span className="mono">PLANDAY_READ_ONLY</span>{' '}
                    in the environment and <span className="mono">allow_writes</span> on this
                    connection.
                  </small>
                </div>
              </div>

              <div className="qrow blocked">
                <Icon name="block" size={18} />
                <div className="grow">
                  Deactivation has no reachable endpoint
                  <small>
                    Six request shapes tried. The Planday web app uses internal services our
                    credentials cannot reach, so termination dates queue here until Planday
                    support answers.
                  </small>
                </div>
              </div>

              {o.lastSync !== null && (
                <div className="qrow done">
                  <Icon name="check" size={18} />
                  <div className="grow">
                    Last read sync {timeOfDay(o.lastSync.finishedAt)}
                    <small>
                      {o.lastSync.seen} seen, {o.lastSync.changed} changed,{' '}
                      {o.lastSync.skipped} skipped.
                    </small>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card title="Departments" aside={<span className="owner mirror">Mirrored</span>}>
            <div className="deptlist">
              {o.departments.map((d) => (
                <div key={d.name} className={d.headcount === 0 ? 'deptrow zero' : 'deptrow'}>
                  <span>{d.name}</span>
                  <span className="n">{d.headcount}</span>
                  <span className="track">
                    {d.headcount > 0 && <i style={{ width: `${(d.headcount / biggest) * 100}%` }} />}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ margin: '14px 0 0', fontSize: 12.5, color: 'var(--ivory-faint)' }}>
              Departments come from Planday and we mirror whatever is there, including entries that
              are really a state rather than a team.
            </p>
          </Card>
        </div>
      </div>
    </div>
  )
}
